import React, { createContext, useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import * as S from './dialogContainer.styles';
import { DialogContainerProps } from './dialogContainer.types';

export const DialogContext = createContext({
  open: false,
  onClose: () => {
    null;
  },
});

/**
 * ***Important**: This component uses portals. If you haven't created a root level container this component won't render! Create an empty div element with the id `dialog-root`. And place it at the top of your app.
 *
 * @param param0.position If `absolute`, the dialog will fit inside the nearest container with `position:relative`. If `fixed`, dialog will fill the whole screen
 * @returns
 */
export const DialogContainer: React.FC<DialogContainerProps> = ({
  position = 'fixed',
  open: propsOpen,
  onClose: propsOnClose,
  children,
}) => {
  const rootElement = document.getElementById('dialog-root');
  const { open: contextOpen, onClose: contextOnClose } = useContext(DialogContext);

  const open = propsOpen ? propsOpen : contextOpen;
  const onClose = propsOnClose ? propsOnClose : contextOnClose;

  const [shouldRender, setShouldRender] = useState(open);

  function render(value: JSX.Element) {
    if (!rootElement || !shouldRender) return null;

    if (position === 'absolute') {
      return value;
    } else if (position === 'fixed') {
      return ReactDOM.createPortal(value, rootElement);
    } else {
      return null;
    }
  }

  useEffect(() => {
    if (open) setShouldRender(true);
  }, [open]);

  const onTransitionEnd = () => {
    if (!open) setShouldRender(false);
  };

  return render(
    <S.Container $position={position}>
      <S.ChildrenWrapper onTransitionEnd={onTransitionEnd} $open={open}>
        {children}
      </S.ChildrenWrapper>
      <S.Overlay $open={open} onClick={() => onClose()} />
    </S.Container>,
  );
};
